import type { Router } from 'vue-router'

// Scroll-linked Blueprint motion. CSS scroll/view timelines drive everything
// where the browser supports them. Otherwise one passive scroll listener and
// one rAF write the same variables from positions cached at measure time.
// Neither path reads layout on a scroll or pointer frame.
const SUBJECTS = '.bp-spec, .bp-notes p, .bp-venue-list li, .bp-h2, .bp-figure, .bp-dims__item, .bp-marks'

export function startBlueprintMotion(router: Router) {
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')

  let stops: { top: number, tick: HTMLElement }[] = []
  let subjects: { el: HTMLElement, top: number, height: number }[] = []
  let grids: HTMLElement[] = []
  let leader: HTMLElement | null = null
  let firstTop = 0
  let leaderSpan = 1
  let sheetLeft = 0
  let sheetTop = 0
  let maxScroll = 1
  let viewH = 0
  let frame = 0
  let mode: 'scroll' | 'fallback' | null = null

  const docTop = (el: HTMLElement) => {
    let y = 0
    let node: HTMLElement | null = el
    while (node) {
      y += node.offsetTop
      node = node.offsetParent as HTMLElement | null
    }
    return y
  }

  const clamp = (n: number) => Math.min(1, Math.max(0, n))

  const measure = () => {
    leader = document.querySelector<HTMLElement>('.bp-leader')
    const sheet = document.querySelector<HTMLElement>('.bp-sheet')
    if (!leader || !sheet) return
    sheetTop = docTop(sheet)
    sheetLeft = sheet.getBoundingClientRect().left

    const raw = [...document.querySelectorAll<HTMLElement>('[data-bp-station]')]
      .map((el) => ({ el, top: docTop(el) }))
      .sort((a, b) => a.top - b.top)
    const stations: { el: HTMLElement, top: number }[] = []
    for (const station of raw) {
      const prev = stations[stations.length - 1]
      if (prev && station.top - prev.top < 140) continue
      stations.push(station)
    }
    const host = leader.querySelector('.bp-leader__ticks')
    host?.replaceChildren()
    stops = []
    const first = stations[0]
    const last = stations[stations.length - 1]
    if (stations.length >= 2 && first && last) {
      firstTop = first.top
      leaderSpan = Math.max(1, last.top - firstTop)
      leader.style.setProperty('--bp-leader-top', `${firstTop - sheetTop}px`)
      leader.style.setProperty('--bp-leader-h', `${leaderSpan}px`)
      leader.dataset.on = ''
      stops = stations.map((station) => {
        const tick = document.createElement('span')
        tick.className = 'bp-leader__tick'
        tick.style.top = `${station.top - firstTop}px`
        host?.appendChild(tick)
        return { top: station.top, tick }
      })
    } else {
      // Pages without a signal path (legal, error) carry no dimension line.
      leader.style.setProperty('--bp-leader-h', '0px')
      delete leader.dataset.on
    }

    subjects = [...document.querySelectorAll<HTMLElement>(SUBJECTS)]
      .map((el) => ({ el, top: docTop(el), height: el.offsetHeight }))
    grids = [...document.querySelectorAll<HTMLElement>('.bp-grid')]
    viewH = window.innerHeight
    maxScroll = Math.max(1, document.documentElement.scrollHeight - viewH)
  }

  const write = () => {
    frame = 0
    if (mode !== 'fallback' || !leader) return
    const y = window.scrollY
    const read = y + viewH * 0.62

    leader.style.setProperty('--bp-p', clamp((read - firstTop) / leaderSpan).toFixed(4))
    for (const stop of stops) {
      const p = clamp((read - stop.top + 40) / 90)
      stop.tick.style.opacity = p.toFixed(3)
      stop.tick.style.transform = `scaleX(${p.toFixed(3)})`
    }

    const sy = clamp(y / maxScroll).toFixed(4)
    for (const grid of grids) grid.style.setProperty('--bp-sy', sy)

    for (const item of subjects) {
      const start = item.top - viewH
      const span = viewH + item.height
      item.el.style.setProperty('--bp-e', clamp((y - start) / (span * 0.4)).toFixed(3))
      item.el.style.setProperty('--bp-c', clamp((y - start) / span).toFixed(3))
    }
  }

  const onScroll = () => {
    if (frame) return
    frame = requestAnimationFrame(write)
  }

  // Crosshair and coordinate readout over measurable parts of the drawing.
  let pointer: PointerEvent | null = null
  let pointerFrame = 0
  let cross: HTMLElement | null = null
  let crossX: HTMLElement | null = null
  let crossY: HTMLElement | null = null
  let readout: HTMLElement | null = null

  const paintPointer = () => {
    pointerFrame = 0
    if (reduceQuery.matches || !pointer || !cross || !crossX || !crossY) {
      cross?.classList.remove('is-on')
      readout?.classList.remove('is-on')
      return
    }
    const target = pointer.target as Element | null
    const hit = !!target?.closest?.('[data-bp-hover]') && !target?.closest?.('.bp-order')
    cross.classList.toggle('is-on', hit)
    readout?.classList.toggle('is-on', hit)
    if (!hit) return
    crossX.style.transform = `translate3d(0, ${pointer.clientY}px, 0)`
    crossY.style.transform = `translate3d(${pointer.clientX}px, 0, 0)`
    if (readout) {
      const x = Math.round((pointer.clientX - sheetLeft) / 8)
      const yy = Math.round((pointer.pageY - sheetTop) / 8)
      readout.textContent = `X ${String(x).padStart(3, '0')}  Y ${String(yy).padStart(4, '0')}`
    }
  }

  const onPointer = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return
    pointer = event
    if (!pointerFrame) pointerFrame = requestAnimationFrame(paintPointer)
  }

  const onPointerLeave = () => {
    cross?.classList.remove('is-on')
    readout?.classList.remove('is-on')
  }

  let listening = false
  let scrollListening = false
  let pointerListening = false
  const root = document.documentElement

  const clearFallback = () => {
    leader?.style.removeProperty('--bp-p')
    for (const stop of stops) {
      stop.tick.style.removeProperty('opacity')
      stop.tick.style.removeProperty('transform')
    }
    for (const grid of grids) grid.style.removeProperty('--bp-sy')
    for (const item of subjects) {
      item.el.style.removeProperty('--bp-e')
      item.el.style.removeProperty('--bp-c')
    }
  }

  const halt = () => {
    mode = null
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    root.classList.remove('bp-js', 'bp-motion', 'bp-scroll', 'bp-fallback')
    clearFallback()
    cross?.classList.remove('is-on')
    readout?.classList.remove('is-on')
  }

  const boot = () => {
    measure()
    if (reduceQuery.matches) {
      halt()
      return
    }
    const supports = typeof CSS !== 'undefined'
      && CSS.supports('animation-timeline: scroll()')
      && CSS.supports('animation-timeline: view()')
    mode = supports ? 'scroll' : 'fallback'
    root.classList.add('bp-js', 'bp-motion', supports ? 'bp-scroll' : 'bp-fallback')
    root.classList.remove(supports ? 'bp-fallback' : 'bp-scroll')
    if (!supports) {
      leader?.style.setProperty('--bp-p', '0')
      write()
    } else {
      clearFallback()
    }
    if (listening) return
    listening = true
    if (!supports && !scrollListening) {
      scrollListening = true
      window.addEventListener('scroll', onScroll, { passive: true })
    }
    if (finePointer.matches && !pointerListening) {
      pointerListening = true
      cross = document.querySelector('.bp-cross')
      crossX = document.querySelector('.bp-cross__x')
      crossY = document.querySelector('.bp-cross__y')
      readout = document.querySelector('.bp-readout')
      document.addEventListener('pointermove', onPointer, { passive: true })
      document.documentElement.addEventListener('pointerleave', onPointerLeave, { passive: true })
    }
  }

  reduceQuery.addEventListener('change', () => {
    boot()
  })

  let timer = 0
  router.afterEach(() => {
    window.clearTimeout(timer)
    timer = window.setTimeout(boot, 60)
  })

  let resizeTimer = 0
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      measure()
      write()
    }, 120)
  }, { passive: true })

  // Fonts and the portrait can shift positions after boot; re-measure once settled.
  const settle = () => {
    measure()
    write()
  }
  if (document.readyState === 'complete') {
    document.fonts?.ready.then(settle)
  } else {
    window.addEventListener('load', settle, { once: true })
  }

  boot()
}
